import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";

import apiClient from "api/apiClient";
import useMessageToast from "hooks/useMessageToast";

const API = process.env.REACT_APP_BASE_URL;

const revokeAllButtonSx = {
  textTransform: "none",
  fontWeight: 700,
  color: "#fff7ed",
  borderColor: "rgba(251, 146, 60, 0.72)",
  backgroundColor: "rgba(194, 65, 12, 0.2)",
  "&:hover": {
    borderColor: "#fdba74",
    backgroundColor: "rgba(194, 65, 12, 0.34)",
  },
  "&.Mui-disabled": {
    color: "rgba(255, 247, 237, 0.42)",
    borderColor: "rgba(251, 146, 60, 0.2)",
    backgroundColor: "rgba(15, 23, 42, 0.18)",
  },
};

const formatDateTime = (value) => {
  if (!value) return "Not available";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return date.toLocaleString();
};

const TrustedDevicesPanel = ({ trustedDeviceEligible = false }) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useMessageToast(error, "error");
  const [status, setStatus] = useState("");
  useMessageToast(status, "info");
  const [revokingId, setRevokingId] = useState("");
  const [revokingAll, setRevokingAll] = useState(false);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const { data } = await apiClient.get(`${API}/user/trusted-devices`, {
        withCredentials: true,
      });
      setDevices(Array.isArray(data?.devices) ? data.devices : []);
    } catch (requestError) {
      setDevices([]);
      setError(requestError?.response?.data?.message || "Failed to load trusted devices.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const handleRevokeDevice = async (deviceId) => {
    setRevokingId(deviceId);
    setStatus("");
    setError("");

    try {
      const { data } = await apiClient.delete(`${API}/user/trusted-devices/${deviceId}`, {
        withCredentials: true,
      });
      setStatus(data?.message || "Trusted device revoked.");
      await loadDevices();
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Failed to revoke trusted device.");
    } finally {
      setRevokingId("");
    }
  };

  const handleRevokeAll = async () => {
    setRevokingAll(true);
    setStatus("");
    setError("");

    try {
      const { data } = await apiClient.delete(`${API}/user/trusted-devices`, {
        withCredentials: true,
      });
      setStatus(data?.message || "Trusted devices revoked.");
      await loadDevices();
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Failed to revoke trusted devices.");
    } finally {
      setRevokingAll(false);
    }
  };

  return (
    <div className="settings-section">
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Trusted devices
          </Typography>
          <Typography color="text.secondary">
            Review remembered browsers that can skip the MFA prompt after password verification.
          </Typography>
        </Box>

        {!trustedDeviceEligible ? (
          <Alert severity="info">
            Trusted devices are disabled for admin and super admin accounts. Privileged access still requires MFA.
          </Alert>
        ) : null}

        {status ? <Alert severity="success">{status}</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Box>
          <Button
            variant="outlined"
            onClick={handleRevokeAll}
            disabled={revokingAll || loading || !devices.length}
            sx={revokeAllButtonSx}
          >
            {revokingAll ? "Revoking..." : "Revoke All Trusted Devices"}
          </Button>
        </Box>

        {loading ? (
          <Stack alignItems="center" py={3}>
            <CircularProgress size={28} />
          </Stack>
        ) : !devices.length ? (
          <Typography color="text.secondary">No active trusted devices.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {devices.map((device) => (
              <Card key={device._id} variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack spacing={1.25}>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      spacing={1.5}
                    >
                      <Box>
                        <Typography fontWeight={700}>{device.deviceLabel || "Trusted browser"}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {device.userAgent || "User agent not available"}
                        </Typography>
                      </Box>
                      <Box>
                        <Button
                          variant="text"
                          color="error"
                          onClick={() => handleRevokeDevice(device._id)}
                          disabled={revokingId === device._id || revokingAll}
                        >
                          {revokingId === device._id ? "Revoking..." : "Revoke"}
                        </Button>
                      </Box>
                    </Stack>

                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                      <Typography variant="body2" color="text.secondary">
                        Created: {formatDateTime(device.createdAt)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Last used: {formatDateTime(device.lastUsedAt)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Expires: {formatDateTime(device.expiresAt)}
                      </Typography>
                      {device.current ? (
                        <Typography variant="body2" fontWeight={700} color="success.main">
                          Current browser
                        </Typography>
                      ) : null}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </div>
  );
};

export default TrustedDevicesPanel;